import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

const ModalContext = createContext(null);

export const ModalProvider = ({ children }) => {
  const [modals, setModals] = useState([]); // { id, render }

  const show = useCallback((render) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setModals((prev) => [...prev, { id, render }]);
    return id;
  }, []);

  const hide = useCallback((id) => {
    setModals((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const ctx = useMemo(() => ({ show, hide }), [show, hide]);

  return (
    <ModalContext.Provider value={ctx}>
      {children}
      {modals.map(({ id, render }) => (
        <ModalPortal key={id}>{render()}</ModalPortal>
      ))}
    </ModalContext.Provider>
  );
};

const ModalPortal = ({ children }) => {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const el = document.createElement("div");
    el.style.position = "fixed";
    el.style.inset = 0;
    el.style.zIndex = 1000;
    el.style.display = "flex";
    el.style.alignItems = "center";
    el.style.justifyContent = "center";
  // No background here; the Modal component handles its own overlay
  // Make container ignore pointer events so it doesn't block the app when empty
  el.style.pointerEvents = "none";
    elRef.current = el;
    document.body.appendChild(el);
  }

  React.useEffect(() => {
    const el = elRef.current;
    return () => {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);

  if (!elRef.current) return null;
  // Re-enable interactions for the actual modal content
  const wrapper = (
    <div style={{ width: "100%", height: "100%", pointerEvents: "auto" }}>
      {children}
    </div>
  );
  return ReactDOM.createPortal(wrapper, elRef.current);
};

// API-compatible hook: useModal(factory, deps?) => [show, hide]
export const useModal = (factory, deps = []) => {
  const ctx = useContext(ModalContext);
  const modalIdRef = useRef(null);

  const show = useCallback(() => {
    if (!ctx) return;
    // If a modal already shown for this hook instance, do nothing
    if (modalIdRef.current) return;
    const id = ctx.show(() => factory());
    modalIdRef.current = id;
  }, [ctx, ...deps]);

  const hide = useCallback(() => {
    if (!ctx) return;
    if (modalIdRef.current) {
      ctx.hide(modalIdRef.current);
      modalIdRef.current = null;
    }
  }, [ctx]);

  return [show, hide];
};

export default {
  ModalProvider,
  useModal,
};
